import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Search, Loader2 } from 'lucide-react'
import * as Icons from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useActions } from '@/shared/hooks/useActions'
import { ConfirmDialog } from './ConfirmDialog'
import { eventBus, setPendingEditTaskId } from '@/shared/lib/eventBus'
import { getTasks, searchNotes, getProjects } from '@/shared/lib/api'
import { useWorkFullscreen } from '@/features/work'

type PaletteCommand = {
  id: string
  label: string
  icon: string
  category: string
  requires_confirmation?: boolean
  is_background?: boolean
  handler?: () => void
}

interface CommandPaletteProps {
  isOpen: boolean
  onClose: () => void
}

export function CommandPalette({
  isOpen,
  onClose,
}: CommandPaletteProps) {
  const navigate = useNavigate()
  const { actions, executeAction, stopAction, isActionRunning, loading } = useActions()
  const [confirmAction, setConfirmAction] = useState<string | null>(null)

  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [searchResults, setSearchResults] = useState<PaletteCommand[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const { openFullscreen: openTaskFullscreen, openProjectsTab } = useWorkFullscreen()

  const navCommands = useMemo<PaletteCommand[]>(() => [
    {
      id: 'nav_home',
      label: 'Go to Dashboard',
      icon: 'LayoutDashboard',
      category: 'Navigation',
      handler: () => { navigate('/'); onClose() },
    },
    {
      id: 'nav_tasks',
      label: 'Go to Tasks',
      icon: 'CheckSquare',
      category: 'Navigation',
      handler: () => { navigate('/'); openTaskFullscreen(); onClose() },
    },
    {
      id: 'nav_projects',
      label: 'Go to Projects',
      icon: 'FolderOpen',
      category: 'Navigation',
      handler: () => { navigate('/'); openProjectsTab(); onClose() },
    },
    {
      id: 'nav_deck',
      label: 'Go to Command Deck',
      icon: 'Workflow',
      category: 'Navigation',
      handler: () => { navigate('/orchestration/deck'); onClose() },
    },
    {
      id: 'nav_forge',
      label: 'Open The Forge',
      icon: 'Hammer',
      category: 'Navigation',
      handler: () => { navigate('/orchestration/forge'); onClose() },
    },
    {
      id: 'nav_library',
      label: 'Open Library',
      icon: 'Library',
      category: 'Navigation',
      handler: () => { navigate('/orchestration/library'); onClose() },
    },
    {
      id: 'nav_visibility',
      label: 'Go to Visibility',
      icon: 'Eye',
      category: 'Navigation',
      handler: () => { navigate('/orchestration/visibility'); onClose() },
    },
    {
      id: 'nav_learning',
      label: 'Go to Learning',
      icon: 'GraduationCap',
      category: 'Navigation',
      handler: () => { navigate('/learning'); onClose() },
    },
    {
      id: 'nav_infrastructure',
      label: 'Go to Infrastructure',
      icon: 'Server',
      category: 'Navigation',
      handler: () => { navigate('/infrastructure'); onClose() },
    },
    {
      id: 'nav_settings',
      label: 'Go to Settings',
      icon: 'Settings',
      category: 'Navigation',
      handler: () => { navigate('/settings'); onClose() },
    },
  ], [navigate, onClose, openTaskFullscreen, openProjectsTab])

  const localCommands = useMemo<PaletteCommand[]>(() => [
    {
      id: 'new_chat',
      label: 'New Chat',
      icon: 'MessageSquarePlus',
      category: 'Chat',
      handler: () => { eventBus.emit('chat:newConversation'); onClose() },
    },
    {
      id: 'add_task',
      label: 'Add Task',
      icon: 'Plus',
      category: 'Tasks',
      handler: () => eventBus.emit('tasks:openForm'),
    },
    {
      id: 'create_note',
      label: 'Create Note',
      icon: 'FilePlus',
      category: 'Notes',
      handler: () => { navigate('/notes/new'); eventBus.emit('notes:createNote'); onClose() },
    },
    {
      id: 'start_deep_work',
      label: 'Start Deep Work',
      icon: 'Play',
      category: 'Actions',
      handler: () => eventBus.emit('deepWork:openForm'),
    },
    {
      id: 'create_project',
      label: 'Create Project',
      icon: 'FolderPlus',
      category: 'Projects',
      handler: () => { navigate('/'); eventBus.emit('projects:createProject'); onClose() },
    },
  ], [navigate, onClose])

  // Convert actions to command items
  const commands = useMemo<PaletteCommand[]>(() => {
    const actionCommands = actions.map(action => ({
      id: action.id,
      label: action.label,
      icon: action.icon,
      category: action.category,
      requires_confirmation: action.requires_confirmation,
      is_background: action.is_background,
    }))

    return [...navCommands, ...localCommands, ...actionCommands]
  }, [navCommands, localCommands, actions])

  const handleCommandExecute = useCallback((command: PaletteCommand) => {
    if (command.handler) {
      command.handler()
      onClose()
      return
    }

    const requiresConfirmation = !!command.requires_confirmation
    const isBackground = !!command.is_background

    // If it's a background action that's running, stop it instead
    if (isBackground && isActionRunning(command.id)) {
      stopAction(command.id)
      onClose()
      return
    }

    if (requiresConfirmation) {
      setConfirmAction(command.id)
    } else {
      executeAction(command.id)
      onClose()
    }
  }, [onClose, isActionRunning, stopAction, executeAction])

  const handleConfirm = () => {
    if (confirmAction) {
      executeAction(confirmAction)
      setConfirmAction(null)
      onClose()
    }
  }

  const getIcon = (iconName: string) => {
    const Icon = (Icons as Record<string, unknown>)[iconName] as React.ComponentType<{ size?: number; className?: string }> | undefined
    return Icon || Search
  }

  // Filter commands based on query and merge with search results
  const filteredStaticCommands = useMemo(() => commands.filter(
    (cmd) =>
      cmd.label.toLowerCase().includes(query.toLowerCase()) ||
      cmd.category.toLowerCase().includes(query.toLowerCase())
  ), [commands, query])

  // Combine static commands with search results
  const filteredCommands = useMemo(() => [...filteredStaticCommands, ...searchResults], [filteredStaticCommands, searchResults])

  // Group commands by category
  const groupedCommands = filteredCommands.reduce(
    (acc, cmd) => {
      if (!acc[cmd.category]) {
        acc[cmd.category] = []
      }
      acc[cmd.category].push(cmd)
      return acc
    },
    {} as Record<string, PaletteCommand[]>
  )

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setSelectedIndex(0)
      setSearchResults([])
      setIsSearching(false)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [isOpen])

  // Live search for tasks and notes
  useEffect(() => {
    if (!isOpen || query.length < 2) {
      setSearchResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)

    // Debounce the search
    const timeoutId = setTimeout(async () => {
      try {
        const results: PaletteCommand[] = []

        // Search tasks
        const tasks = await getTasks()
        const matchingTasks = tasks
          .filter(task =>
            task.title.toLowerCase().includes(query.toLowerCase()) ||
            task.description?.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 5) // Limit to 5 results

        matchingTasks.forEach(task => {
          results.push({
            id: `task_${task.id}`,
            label: task.title,
            icon: task.status === 'done' ? 'CheckCircle2' : task.status === 'in_progress' ? 'Circle' : 'Circle',
            category: 'Tasks',
            handler: () => {
              setPendingEditTaskId(task.id)
              eventBus.emit('tasks:openEditForm')
              navigate('/')
              onClose()
            },
          })
        })

        // Search projects
        const projects = await getProjects()
        const matchingProjects = projects
          .filter(project =>
            project.name.toLowerCase().includes(query.toLowerCase()) ||
            project.description?.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 5)

        matchingProjects.forEach(project => {
          results.push({
            id: `project_${project.id}`,
            label: project.name,
            icon: 'FolderOpen',
            category: 'Projects',
            handler: () => { navigate(`/projects/${project.id}`); onClose() },
          })
        })

        // Search notes
        const notes = await searchNotes(query)
        const matchingNotes = notes.slice(0, 5) // Limit to 5 results

        matchingNotes.forEach(note => {
          results.push({
            id: `note_${note.id}`,
            label: note.title || 'Untitled Note',
            icon: 'FileText',
            category: 'Notes',
            handler: () => { navigate(`/notes/${note.id}`); onClose() },
          })
        })

        setSearchResults(results)
      } catch (error) {
        console.error('Search error:', error)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 200)

    return () => clearTimeout(timeoutId)
  // onClose is stable (useCallback in useCommandPalette) but is not a reactive
  // trigger for re-running the search — intentionally omitted from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isOpen, navigate])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((i) =>
            i < filteredCommands.length - 1 ? i + 1 : 0
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((i) =>
            i > 0 ? i - 1 : filteredCommands.length - 1
          )
          break
        case 'Enter':
          e.preventDefault()
          {
            const selectedCmd = filteredCommands[selectedIndex]
            if (selectedCmd) {
              handleCommandExecute(selectedCmd)
            }
          }
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, filteredCommands, selectedIndex, handleCommandExecute])

  if (!isOpen) return null

  let flatIndex = -1

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-[#111111] border border-white/5 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center px-4 py-3 border-b border-white/5 gap-3">
          {isSearching ? (
            <Loader2 size={18} className="text-white/30 animate-spin" />
          ) : (
            <Search size={18} className="text-white/30" />
          )}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setSelectedIndex(0)
            }}
            placeholder="Search commands, tasks, projects, notes..."
            className="flex-1 bg-transparent outline-none text-white/90 placeholder:text-white/20 text-sm"
          />
          <span className="text-[10px] bg-white/5 border border-white/5 text-white/40 px-1.5 py-0.5 rounded font-mono">
            ESC
          </span>
        </div>

        {/* Command List */}
        <div className="max-h-[300px] overflow-y-auto p-2">
          {Object.entries(groupedCommands).map(([category, cmds]) => (
            <div key={category}>
              <div className="px-2 py-1.5 text-[10px] font-bold text-white/30 uppercase tracking-wider">
                {category}
              </div>
              {cmds.map((cmd) => {
                flatIndex++
                const isSelected = flatIndex === selectedIndex
                const Icon = getIcon(cmd.icon)
                const isRunning = isActionRunning(cmd.id)
                const showStop = cmd.is_background && isRunning

                return (
                  <div
                    key={cmd.id}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer group transition-colors text-sm ${
                      isSelected
                        ? 'bg-white/10 text-white'
                        : 'text-white/60 hover:bg-white/5 hover:text-white'
                    } ${loading && !isRunning ? 'opacity-50 cursor-not-allowed' : ''} ${
                      isRunning && cmd.is_background ? 'bg-purple-400/10 border-l-2 border-purple-400/50' : ''
                    }`}
                    onClick={() => {
                      if (!loading || isRunning) {
                        handleCommandExecute(cmd)
                      }
                    }}
                  >
                    <Icon size={16} />
                    <span className="flex-1">{showStop ? `Stop ${cmd.label}` : cmd.label}</span>
                    {isRunning && cmd.is_background && (
                      <span className="text-xs text-purple-400 animate-pulse">● Running</span>
                    )}
                  </div>
                )
              })}
            </div>
          ))}

          {filteredCommands.length === 0 && (
            <div className="px-3 py-8 text-center text-white/40">
              {commands.length === 0 ? 'Loading commands...' : 'No commands found'}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-white/5 bg-white/[0.02] flex justify-end items-center text-xs text-white/30">
          <div className="flex gap-3">
            <span>↑↓ Navigate</span>
            <span>↵ Select</span>
            <span>ESC Close</span>
          </div>
        </div>
      </div>

      {confirmAction && (
        <ConfirmDialog
          isOpen={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirm}
          title="Confirm Action"
          message={`Are you sure you want to execute "${commands.find(c => c.id === confirmAction)?.label}"?`}
        />
      )}
    </div>
  )
}

